import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Company, CompanySalaryTransaction, CompanySalarySummary } from "../types";
import { companiesAPI, companySalaryAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";
import { ArrowLeft, Plus, IndianRupee, Calendar, Trash2, TrendingUp, PiggyBank } from "lucide-react";

const CompanySalary: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const [company, setCompany] = useState<Company | null>(null);
  const [transactions, setTransactions] = useState<CompanySalaryTransaction[]>([]);
  const [summary, setSummary] = useState<CompanySalarySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    amount: "",
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    taxDeducted: "",
    notes: "",
  });

  useEffect(() => {
    if (companyId) {
      fetchData();
    }
  }, [companyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    if (!companyId) return;

    try {
      setLoading(true);
      const [companyData, transactionsData, summaryData] = await Promise.all([
        companiesAPI.getCompany(companyId),
        companySalaryAPI.getSalaryTransactions(companyId),
        companySalaryAPI.getSalarySummary(companyId),
      ]);
      setCompany(companyData);
      setTransactions(transactionsData);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.amount || !formData.month || !formData.year) {
      setError("Amount, month, and year are required");
      return;
    }

    const amount = parseFloat(formData.amount);
    const taxDeducted = formData.taxDeducted ? parseFloat(formData.taxDeducted) : 0;
    
    if (isNaN(amount) || amount <= 0) {
      setError(`Please enter a valid ${company?.type === 'internship' ? 'stipend' : 'salary'} amount`);
      return;
    }

    try {
      setError("");
      await companySalaryAPI.createSalaryTransaction({
        company: companyId!,
        amount,
        month: formData.month,
        year: formData.year,
        taxDeducted,
        notes: formData.notes.trim(),
      });

      resetForm();
      fetchData();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to add ${company?.type === 'internship' ? 'stipend' : 'salary'} transaction`);
    }
  };

  const handleDelete = async (transactionId: string) => {
    if (window.confirm(`Are you sure you want to delete this ${company?.type === 'internship' ? 'stipend' : 'salary'} transaction?`)) {
      try {
        await companySalaryAPI.deleteSalaryTransaction(transactionId);
        fetchData();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete transaction");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      amount: "",
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      taxDeducted: "",
      notes: "",
    });
    setShowAddForm(false);
    setError("");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getMonthName = (month: number) => {
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return months[month - 1];
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="p-6 max-w-7xl mx-auto">
          <LoadingSkeleton variant="table" count={5} />
        </div>
      </PageTransition>
    );
  }

  if (!company) {
    return (
      <div className="p-6 max-w-7xl mx-auto text-left">
        <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
          <h3 className="text-xl font-semibold text-gray-900 mb-2 text-left">Company not found</h3>
          <Link to="/companies" className="text-red-600 hover:text-red-700 text-left">
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <PageTransition>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/companies"
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 mb-4 justify-start text-left"
          >
            <ArrowLeft className="w-4 h-4 text-left" />
            <span className="text-left">Back to Companies</span>
          </Link>
          
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {company.name} - {company.type === 'internship' ? 'Stipend' : 'Salary'} Details
              </h1>
              <p className="text-gray-600 mt-2">
                Manage {company.type === 'internship' ? 'stipend' : 'salary'} transactions and view summaries
              </p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5" />
              Add {company.type === 'internship' ? 'Stipend' : 'Salary'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-green-500 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Total Credited</h3>
                  <p className="text-3xl font-bold text-left">{formatCurrency(summary.totalCredited)}</p>
                </div>
                <TrendingUp className="w-12 h-12 opacity-80" />
              </div>
            </div>
            <div className="bg-red-500 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Total Tax Deducted</h3>
                  <p className="text-3xl font-bold text-left">{formatCurrency(summary.totalTax)}</p>
                </div>
                <PiggyBank className="w-12 h-12 opacity-80" />
              </div>
            </div>
            <div className="bg-indigo-500 text-white p-6 rounded-2xl shadow-lg">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Net Received</h3>
                  <p className="text-3xl font-bold text-left">{formatCurrency(summary.totalCredited - summary.totalTax)}</p>
                </div>
                <IndianRupee className="w-12 h-12 opacity-80" />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Add Salary Form */}
        {showAddForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                Add {company.type === 'internship' ? 'Stipend' : 'Salary'} Transaction
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {company.type === 'internship' ? 'Stipend' : 'Salary'} Amount *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax Deducted
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.taxDeducted}
                      onChange={(e) => setFormData({ ...formData, taxDeducted: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Month *
                    </label>
                    <select
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {getMonthName(i + 1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Year *
                    </label>
                    <input
                      type="number"
                      min="2000"
                      required
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    placeholder={`Additional notes about this ${company.type === 'internship' ? 'stipend' : 'salary'}...`}
                  />
                </div>
                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors"
                  >
                    Add {company.type === 'internship' ? 'Stipend' : 'Salary'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Salary Transactions */}
        {transactions.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No {company.type === 'internship' ? 'stipend' : 'salary'} records yet
            </h3>
            <p className="text-gray-600 mb-6">
              Start by adding your first {company.type === 'internship' ? 'stipend' : 'salary'} transaction
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-green-600 text-white px-6 py-3 rounded-xl hover:bg-green-700 transition-colors"
            >
              Add {company.type === 'internship' ? 'Stipend' : 'Salary'}
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">
                {company.type === 'internship' ? 'Stipend' : 'Salary'} History ({transactions.length} transactions)
              </h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Month/Year
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tax Deducted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Net Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notes
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr key={transaction._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Calendar className="w-5 h-5 text-gray-400 mr-2" />
                          <span className="text-sm font-medium text-gray-900">
                            {getMonthName(transaction.month)} {transaction.year}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-green-600">
                          {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-red-600">
                          {formatCurrency(transaction.taxDeducted)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-red-600">
                          {formatCurrency(transaction.amount - transaction.taxDeducted)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">
                          {transaction.notes || "No notes"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleDelete(transaction._id)}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default CompanySalary;